import { openDB, type IDBPDatabase } from "idb";
import type { AssetMetaResp } from "@pix/shared";
import { api } from "../api/client";

/**
 * アセットの永続化:
 *   - クラウド(API + R2 + D1)が正本
 *   - ローカル IndexedDB はキャッシュ(オフライン読み込み + 高速プレビュー)
 *
 * IndexedDB key: `${projectId}:${path}`
 */

const DB_NAME = "pix-studio";
const DB_VERSION = 2;
const STORE = "assets-v2";

export type AssetCategory = "bg" | "chara" | "face" | "bgm" | "se" | "voice" | "other";

export interface AssetRecord {
  projectId: string;
  path: string;
  mime: string;
  size: number;
  blob: Blob;
  category: AssetCategory;
  updatedAt: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, _oldVersion) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "key" });
          store.createIndex("projectId", "projectId");
        }
      },
    });
  }
  return dbPromise;
}

interface RawRecord extends AssetRecord {
  key: string;
}

function keyOf(projectId: string, path: string): string {
  return `${projectId}:${path}`;
}

async function cacheAsset(rec: AssetRecord): Promise<void> {
  const db = await getDb();
  const raw: RawRecord = { ...rec, key: keyOf(rec.projectId, rec.path) };
  await db.put(STORE, raw);
}

async function cacheDelete(projectId: string, path: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, keyOf(projectId, path));
}

export async function cacheListByProject(projectId: string): Promise<AssetRecord[]> {
  const db = await getDb();
  const idx = db.transaction(STORE).store.index("projectId");
  const raws = (await idx.getAll(projectId)) as RawRecord[];
  return raws
    .map((r) => ({
      projectId: r.projectId,
      path: r.path,
      mime: r.mime,
      size: r.size,
      blob: r.blob,
      category: r.category,
      updatedAt: r.updatedAt,
    }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

// ===== クラウド連携 =====

export async function uploadAsset(
  projectId: string,
  path: string,
  blob: Blob,
  mime: string,
): Promise<AssetRecord> {
  await api.uploadAsset(projectId, path, blob);
  const rec: AssetRecord = {
    projectId,
    path,
    mime: mime || blob.type || "application/octet-stream",
    size: blob.size,
    blob,
    category: inferCategoryFromPath(path),
    updatedAt: Date.now(),
  };
  await cacheAsset(rec);
  return rec;
}

export async function deleteAsset(projectId: string, path: string): Promise<void> {
  await api.deleteAsset(projectId, path);
  await cacheDelete(projectId, path);
}

/** クラウドのアセット一覧と同期(差分ダウンロード) */
export async function syncAssets(projectId: string): Promise<AssetRecord[]> {
  const remote: AssetMetaResp[] = await api.listAssets(projectId);
  const local = await cacheListByProject(projectId);
  const localMap = new Map(local.map((a) => [a.path, a]));

  for (const r of remote) {
    const l = localMap.get(r.path);
    if (!l || l.updatedAt < r.updatedAt) {
      const blob = await api.downloadAsset(projectId, r.path);
      await cacheAsset({
        projectId,
        path: r.path,
        mime: r.mime,
        size: r.size,
        blob,
        category: inferCategoryFromPath(r.path),
        updatedAt: r.updatedAt,
      });
    }
  }
  const remotePaths = new Set(remote.map((r) => r.path));
  for (const l of local) {
    if (!remotePaths.has(l.path)) {
      await cacheDelete(projectId, l.path);
    }
  }
  return cacheListByProject(projectId);
}

export async function listAssets(projectId: string): Promise<AssetRecord[]> {
  return cacheListByProject(projectId);
}

export async function buildDataUrlDict(projectId: string): Promise<Record<string, string>> {
  const all = await cacheListByProject(projectId);
  const out: Record<string, string> = {};
  for (const a of all) {
    out[a.path] = await blobToDataUrl(a.blob);
  }
  return out;
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export function inferCategoryFromPath(path: string): AssetCategory {
  const lower = path.toLowerCase();
  if (lower.startsWith("bg/") || lower.includes("/bg/")) return "bg";
  if (lower.startsWith("chara/") || lower.includes("/chara/")) return "chara";
  if (lower.startsWith("face/") || lower.includes("/face/")) return "face";
  if (lower.startsWith("bgm/") || lower.includes("/bgm/")) return "bgm";
  if (lower.startsWith("se/") || lower.includes("/se/")) return "se";
  if (lower.startsWith("voice/") || lower.includes("/voice/")) return "voice";
  return "other";
}

export function inferCategoryFromMime(mime: string): AssetCategory {
  if (mime.startsWith("audio/")) return "se";
  if (mime.startsWith("image/")) return "bg";
  return "other";
}

export function defaultPathForFile(file: File, category: AssetCategory): string {
  const name = file.name.replace(/^\.+/, "");
  if (category === "other") return name;
  return `${category}/${name}`;
}
